import styled from '@emotion/styled';
import { Checkbox, Select } from 'antd';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line as Line2,
  LineChart,
  ResponsiveContainer,
  Tooltip as Tooltip2,
  XAxis,
  YAxis,
} from 'recharts';
import { TitreEntity } from '../../../../../entities/titre.entity';

const Container = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
  box-sizing: border-box;
  padding: 10px;
  color: var(--mt-text);

  .curve-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 10px;
  }

  .curve-title span,
  .control-label {
    display: block;
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .curve-title strong {
    display: block;
    margin-top: 3px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 16px;
  }

  .curve-control {
    width: 190px;
  }

  .chart-shell {
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 160px;
    gap: 12px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background-color: var(--mt-card);
    padding: 10px;
  }

  .chart-area {
    min-width: 0;
    min-height: 260px;
  }

  .legend-panel {
    border-left: 1px solid var(--mt-border);
    padding-left: 12px;
  }

  .legend-title {
    display: block;
    margin-bottom: 8px;
    color: var(--mt-warning);
    font-family: Poppins-B;
    font-size: 11px;
    text-transform: uppercase;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 7px;
    font-size: 12px;
  }

  .empty-state {
    height: 100%;
    min-height: 220px;
    display: grid;
    place-items: center;
    color: var(--mt-muted);
    border: 1px dashed var(--mt-border);
    border-radius: 8px;
    background-color: var(--mt-surface);
    text-align: center;
  }

  @media (max-width: 760px) {
    .curve-toolbar,
    .chart-shell {
      grid-template-columns: 1fr;
      flex-direction: column;
    }

    .legend-panel {
      border-left: 0;
      border-top: 1px solid var(--mt-border);
      padding-left: 0;
      padding-top: 10px;
    }
  }
`;

interface countryState {
  [key: string]: boolean;
}

const sortFn = (
  a: { category: string; value: number },
  b: { category: string; value: number },
) => moment(a.category).valueOf() - moment(b.category).valueOf();

const getPaymentFrequency = (frequency?: string) => {
  const label = (frequency || '').toLowerCase();

  if (label.includes('mens')) return 12;
  if (label.includes('trimes')) return 4;
  if (label.includes('semes') || label.includes('semi')) return 2;
  return 1;
};

const getMarketPrice = (item: TitreEntity) => {
  const titre = item as TitreEntity & {
    ask?: number;
    bid?: number;
    prix?: number;
    prixNominale?: number;
  };
  const bid = Number(titre.bid);
  const ask = Number(titre.ask);
  const price = Number(titre.prix || titre.prixNominale);
  const faceValue = Number(titre.valeurNominale) || 10000;

  if (Number.isFinite(bid) && bid > 0 && Number.isFinite(ask) && ask > 0) {
    return (bid + ask) / 2;
  }

  if (Number.isFinite(bid) && bid > 0) return bid;
  if (Number.isFinite(ask) && ask > 0) return ask;
  if (Number.isFinite(price) && price > 0) return price;

  // Fallback conservateur: si aucun prix de marché n'est disponible,
  // on valorise au pair pour éviter une courbe vide.
  return faceValue;
};

const getCashflows = (
  item: TitreEntity,
  settlementDate: moment.Moment,
  faceValue: number,
  couponRate: number,
) => {
  const maturity = moment(item.dateMaturite);
  const frequency = getPaymentFrequency(item.frequencePaiement);
  const monthsStep = 12 / frequency;
  const coupon = (faceValue * couponRate) / frequency;
  const dates: moment.Moment[] = [];
  let payDate = maturity.clone();

  while (payDate.isAfter(settlementDate, 'day')) {
    dates.push(payDate.clone());
    payDate = payDate.subtract(monthsStep, 'months');
  }

  return dates
    .reverse()
    .map((date, index, arr) => ({
      amount: coupon + (index === arr.length - 1 ? faceValue : 0),
      time: Math.max(date.diff(settlementDate, 'days') / 365, 1 / 365),
    }))
    .filter((cashflow) => cashflow.amount > 0 && cashflow.time > 0);
};

const calculateYieldToMaturity = (item: TitreEntity) => {
  const settlementDate = moment();
  const maturity = moment(item.dateMaturite);
  const faceValue = Number(item.valeurNominale) || 10000;
  const couponRate = Number(item.tauxMarginal) / 100;
  const marketPrice = getMarketPrice(item);

  if (
    !maturity.isValid() ||
    !maturity.isAfter(settlementDate, 'day') ||
    !Number.isFinite(couponRate) ||
    couponRate < 0 ||
    !Number.isFinite(marketPrice) ||
    marketPrice <= 0
  ) {
    return null;
  }

  const cashflows = getCashflows(item, settlementDate, faceValue, couponRate);

  if (!cashflows.length) return null;

  let low = -0.95;
  let high = 1;

  const presentValue = (yieldRate: number) =>
    cashflows.reduce(
      (total, cashflow) =>
        total + cashflow.amount / Math.pow(1 + yieldRate, cashflow.time),
      0,
    );

  while (presentValue(high) > marketPrice && high < 10) {
    high *= 2;
  }

  for (let i = 0; i < 80; i++) {
    const mid = (low + high) / 2;
    const pv = presentValue(mid);

    if (pv > marketPrice) low = mid;
    else high = mid;
  }

  const ytm = ((low + high) / 2) * 100;
  return Number.isFinite(ytm) ? ytm : null;
};

const yieldCurveData = (data: TitreEntity[], pays: string) => {
  const totals: { [key: string]: number } = {};
  const counts: { [key: string]: number } = {};

  for (let item of data) {
    if (pays !== 'CEMAC' && item.pays !== pays) continue;

    const maturity = moment(item.dateMaturite);
    const ytm = calculateYieldToMaturity(item);

    if (!maturity.isValid() || ytm === null) continue;

    const key = maturity.format('YYYY-MM');
    totals[key] = (totals[key] || 0) + ytm;
    counts[key] = (counts[key] || 0) + 1;
  }

  const result = Object.keys(totals)
    .map((category) => ({
      category,
      value: Number((totals[category] / counts[category]).toFixed(2)),
    }))
    .sort(sortFn);

  return {
    name: pays,
    data: result,
  };
};

const DEFAULT_COUNTRY_ORDER = [
  'Cameroun',
  'Gabon',
  'Tchad',
  'Centrafrique',
  'Congo',
  'Guinée Eq.',
];

const lineColors: { [cle: string]: string } = {
  Gabon: '#ff4f1f',
  Cameroun: '#ffb000',
  Congo: '#008f4f',
  Tchad: '#7c3aed',
  'Guinée Eq.': '#14b8a6',
  Centrafrique: '#d92d20',
  CEMAC: '#0f1d29',
};

export const Courbes = ({
  titresData,
  getTitre,
}: {
  titresData: TitreEntity[];
  getTitre: (b: TitreEntity, ope: '1' | '2') => void;
}) => {
  const [periodicite, setPeriodicite] = useState(titresData);
  const [periode, setPeriode] = useState({
    key: '',
    value: 'Historique complet',
  });
  const [visiblestate, setVisible] = useState<countryState>({
    CEMAC: true,
  });

  const countryOptions = useMemo(() => {
    const countries = Array.from(
      new Set(
        titresData
          .filter((titre) => titre.pays && calculateYieldToMaturity(titre) !== null)
          .map((titre) => titre.pays),
      ),
    );

    return [
      ...DEFAULT_COUNTRY_ORDER.filter((country) => countries.includes(country)),
      ...countries
        .filter((country) => !DEFAULT_COUNTRY_ORDER.includes(country))
        .sort((a, b) => a.localeCompare(b)),
      'CEMAC',
    ];
  }, [titresData]);

  const [series, setSeries] = useState(
    countryOptions.map((country) => yieldCurveData(periodicite, country)),
  );

  useEffect(() => {
    setVisible((prevState) => {
      const nextState = { ...prevState };
      countryOptions.forEach((country) => {
        if (nextState[country] === undefined) nextState[country] = true;
      });
      return nextState;
    });
  }, [countryOptions]);

  useEffect(() => {
    if (periode.key) {
      switch (periode.key) {
        case '3 semaines':
          return setPeriodicite(
            titresData.filter(
              (e) =>
                moment(e.dateMaturite).diff(moment(), 'weeks') <= 3 &&
                moment(e.dateMaturite).diff(moment(), 'days') >= 0,
            ),
          );
        case '1 mois':
          return setPeriodicite(
            titresData.filter(
              (e) =>
                moment(e.dateMaturite).diff(moment(), 'months') <= 1 &&
                moment(e.dateMaturite).diff(moment(), 'days') >= 0,
            ),
          );
        case '6 mois':
          return setPeriodicite(
            titresData.filter(
              (e) =>
                moment(e.dateMaturite).diff(moment(), 'months') <= 6 &&
                moment(e.dateMaturite).diff(moment(), 'days') >= 0,
            ),
          );
        case '1 an':
          return setPeriodicite(
            titresData.filter(
              (e) =>
                moment(e.dateMaturite).diff(moment(), 'months') <= 12 &&
                moment(e.dateMaturite).diff(moment(), 'days') >= 0,
            ),
          );
      }
    } else {
      setPeriodicite(titresData);
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode]);

  useEffect(() => {
    setSeries(
      countryOptions.map((country) => yieldCurveData(periodicite, country)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodicite, countryOptions]);

  const handleChange = (e: any) => {
    setVisible((prevState) => ({
      ...prevState,
      [e.target.id]: !prevState[e.target.id],
    }));
  };
  const visibleSeries = useMemo(
    () => series.filter((e) => visiblestate[e.name] && e.data.length > 0),
    [series, visiblestate],
  );

  return (
    <Container>
      <div className='curve-toolbar'>
        <div className='curve-title'>
          <span>Courbe de taux actuariels</span>
          <strong>YTM moyen par maturité</strong>
        </div>
        <div className='curve-control'>
          <span className='control-label'>Historique</span>
          <Select
            options={[
              { key: '3 semaines', value: '3 semaines', label: '3 semaines' },
              { key: '1 mois', value: '1 mois', label: '1 mois' },
              { key: '6 mois', value: '6 mois', label: '6 mois' },
              { key: '1 an', value: '1 an', label: '1 an' },
              { key: '', value: '', label: 'Historique complet' },
            ]}
            value={periode.key}
            onChange={(value) =>
              setPeriode({
                key: value,
                value: value || 'Historique complet',
              })
            }
            size='middle'
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>
      </div>

      <div className='chart-shell'>
        <div className='chart-area'>
          {visibleSeries.length === 0 ? (
            <div className='empty-state'>
              Aucune donnée disponible pour la période sélectionnée
            </div>
          ) : (
            <ResponsiveContainer width='100%' height='100%'>
              <LineChart margin={{ bottom: 34, top: 14, right: 20, left: 4 }}>
                <CartesianGrid strokeDasharray='3 3' stroke='rgba(148, 163, 184, 0.35)' />
                <XAxis
                  dataKey='category'
                  allowDuplicatedCategory={false}
                  angle={-35}
                  textAnchor='end'
                  height={58}
                  tick={{ fill: 'var(--mt-muted)', fontSize: 11 }}
                />
                <YAxis
                  dataKey='value'
                  tick={{ fill: 'var(--mt-muted)', fontSize: 11 }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip2
                  formatter={(value: any) => [`${value}%`, 'YTM moyen']}
                  labelFormatter={(label) => `Maturité ${label}`}
                />
                {visibleSeries.map((s) => (
                  <Line2
                    dataKey='value'
                    type='monotone'
                    data={s.data}
                    stroke={lineColors[s.name] || '#ffb000'}
                    strokeWidth={2.5}
                    dot={{ r: 3, strokeWidth: 1.5 }}
                    activeDot={{ r: 5 }}
                    name={s.name}
                    key={s.name}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className='legend-panel'>
          <span className='legend-title'>Marchés</span>
          {countryOptions.map((country) => (
            <div className='legend-item' key={country}>
              <Checkbox
                id={country}
                checked={visiblestate[country]}
                onChange={handleChange}
                style={{ marginRight: 3 }}
              />
              <label
                style={{ cursor: 'pointer', color: lineColors[country] }}
                htmlFor={`${country}`}
              >
              {country}
              </label>
            </div>
          ))}
        </div>
      </div>
    </Container>
  );
};
